import React, { Fragment, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { RaisedButton, IconButton, Chip } from 'material-ui';
import CreateIcon from 'material-ui/svg-icons/content/create';
import DeleteIcon from 'material-ui/svg-icons/action/delete';
import AddIcon from 'material-ui/svg-icons/content/add';
import InfoIcon from 'material-ui/svg-icons/action/info-outline';

import StevedoringPopover from './StevedoringPopover';
import AddServiceProvider from './AddServiceProvider';
import EditServiceProvider from './EditServiceProvider';
import { IsEditModeOnContext } from '../ServiceProviders';
import { getRecommendedTitle } from 'helpers/ports';

const styles = {
    btn: {
        style: { margin: '20px 0 0 0' },
        labelStyle: { textTransform: 'none', fontSize: '13px' },
    },
};

const ServiceProvidersList = ({ 
    serviceProviders, 
    handleChangeServiceProviders,
}) => {
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [currentProvider, setCurrentProvider] = useState(null);

    const isEditModeOn = useContext(IsEditModeOnContext);

    const hasRecommended = React.useMemo(
        () => (Array.isArray(serviceProviders)
            ? serviceProviders.find(s => s.recommended && s.recommended.endAt)
            : null),
        [serviceProviders],
    );

    console.log(hasRecommended);

    const handleAdd = (newProvider) => {
        handleChangeServiceProviders([...serviceProviders, newProvider]);
    };

    const handleEdit = (editedProvider) => {
        const index = serviceProviders.indexOf(currentProvider);
        if (index !== -1) {
            const updatedProviders = [...serviceProviders];
            updatedProviders[index] = editedProvider;
            handleChangeServiceProviders(updatedProviders);
        }
    };

    const handleDelete = (index) => {
        handleChangeServiceProviders(serviceProviders.filter((_, i) => i !== index));
    };

    const emptyServiceProvider = {
        activities: [],
        name: '',
        address: '',
        PIC: '',
        emails: [''],
        phones: [{
            phone: '',
            code: '',
        }],
        limitations: [{
            maxDraught: undefined,
            maxBeam: undefined,
            maxLOA: undefined,
            maxDWT: undefined,
            channelDepth: undefined,
            anchorageDepth: undefined,
        }],
        companyId: undefined,
        contactPersonId: undefined,
        recommended: undefined,
    };
    
    const handleEditClick = (provider) => {
        setCurrentProvider(provider);
        setOpenEditDialog(true);
    };

    return (
        <Fragment>
            <div className="table">
                <div className="table-header">
                    <div className="table-row">
                        <div className="table-col" style={{ width: '169px' }}>Activities</div>
                        <div className="table-col" style={{ width: '168px' }}>Company Name</div>
                        <div className="table-col">Contact Details</div>
                    </div>
                </div>

                <div className="table-body">
                    {serviceProviders.map((serviceProvider, index) => {
                        if (!serviceProvider) return null;

                        return (
                            <div className="table-row" key={`${serviceProvider._id}-${index}`}>
                                <div className="table-col info">
                                    <div className='name'>
                                        {serviceProvider.activities && serviceProvider.activities.includes('Stevedoring Company') ? (
                                            <StevedoringPopover serviceProvider={serviceProvider} />
                                        ) : (
                                            <div>
                                                {serviceProvider.activities && serviceProvider.activities.map((activity, idx) => (
                                                    <div key={`activity_${idx}`}>
                                                        <span>{activity}</span><br />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div className="table-col">
                                    <div className='name company'>
                                        {serviceProvider.name}
                                    </div>
                                </div>
                                <div className="table-col">
                                    <div className="list">
                                        {serviceProvider.emails.map((email, emailIndex) => (
                                            <div key={emailIndex} className='email'>{email}</div>
                                        ))}
                                    </div>

                                    <div className="list">
                                        {serviceProvider.phones.map((phone, phoneIndex) => (
                                            <div key={phoneIndex} className='phone'>
                                                {`${phone.code} ${phone.phone}`}
                                            </div>
                                        ))}
                                    </div>

                                    <div className='name'>{serviceProvider.address}</div>
                                    <div className='name'>PIC: {serviceProvider.PIC}</div>
                                </div>
                                <div
                                    className="table-col edit"
                                    style={{
                                        display: 'flex', alignItems: 'center', position: 'relative', width: '144px',
                                    }}
                                >
                                    {serviceProvider.recommended && serviceProvider.recommended.endAt ? (
                                        <Chip
                                            style={{
                                                backgroundColor: '#E8F5E9',
                                                height: 24,
                                                position: 'absolute',
                                                top: 10,
                                                right: 0,
                                                zIndex: 1000,
                                                width: 118,
                                            }}
                                            labelStyle={{
                                                color: '#2E7D32',
                                                lineHeight: '24px',
                                                fontSize: '12px',
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            Recommended
                                            <IconButton
                                                tooltip={getRecommendedTitle(serviceProvider)}
                                                tooltipPosition='bottom-left'
                                                style={{ width: 20, height: 18, padding: 0 }}
                                                iconStyle={{ width: 16, height: 16, color: '#2E7D32' }}
                                            >
                                                <InfoIcon />
                                            </IconButton>
                                        </Chip>
                                    ) : null}
                                    {
                                        isEditModeOn ? (
                                            <div style={{ display: 'flex', width: '100%', justifyContent: 'space-evenly' }}>
                                                <div className="icon edit-icon">
                                                    <IconButton
                                                        iconStyle={{ color: '#CBCBCB' }}
                                                        onClick={() => handleEditClick(serviceProvider)}
                                                    >
                                                        <CreateIcon />
                                                    </IconButton>
                                                </div>

                                                <div className="icon delete-icon">
                                                    <IconButton
                                                        iconStyle={{ color: '#CBCBCB' }}
                                                        onClick={() => handleDelete(index)}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </div>
                                            </div>
                                        ) : null
                                    }
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {isEditModeOn && (
                <React.Fragment>
                    <RaisedButton
                        label="Add Service Provider"
                        labelStyle={styles.btn.labelStyle}
                        style={styles.btn.style}
                        labelPosition="before"
                        icon={<AddIcon />}
                        primary
                        onClick={() => setOpenAddDialog(true)}
                    />

                    <div>
                        <EditServiceProvider
                            key={currentProvider && currentProvider._id}
                            open={openEditDialog}
                            onClose={() => {
                                setOpenEditDialog(false);
                                setCurrentProvider(null);
                            }}
                            onSubmit={handleEdit}
                            serviceProvider={currentProvider}
                            hasRecommended={hasRecommended}
                        />
                    </div>

                    <AddServiceProvider
                        open={openAddDialog}
                        onClose={() => setOpenAddDialog(false)}
                        onSubmit={handleAdd}
                        initialValues={emptyServiceProvider}
                        hasRecommended={hasRecommended}
                    />
                </React.Fragment>
            )}
        </Fragment>
    );
};

ServiceProvidersList.propTypes = {
    serviceProviders: PropTypes.arrayOf(PropTypes.shape({
        activities: PropTypes.arrayOf(PropTypes.string),
        name: PropTypes.string,
        address: PropTypes.string,
        PIC: PropTypes.string,
        emails: PropTypes.arrayOf(PropTypes.string),
        phones: PropTypes.arrayOf(PropTypes.shape({
            code: PropTypes.string,
            phone: PropTypes.string,
        })),
        limitations: PropTypes.arrayOf(PropTypes.shape({
            berth: PropTypes.string,
            maxDraught: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            maxBeam: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            maxLOA: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            maxDWT: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            channelDepth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            anchorageDepth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })),
    })),
    handleChangeServiceProviders: PropTypes.func.isRequired,
};

ServiceProvidersList.defaultProps = {
    serviceProviders: [],
};

export default ServiceProvidersList;



// WEBPACK FOOTER //
// ./app/components/Regions/PortManagement/blocks/ServiceProviders/ServiceProvidersView/ServiceProvidersList.jsx